import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Typography
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import api from "../../services/api";

const DEFAULT_SEARCH_OPTIONS = {
  enabledFuzzy: true,
  confidenceThreshold: 0.82,
  mediumConfidenceThreshold: 0.62,
  askConfirmationOnMedium: true,
  enableAliases: true,
  ignoreAccents: true,
  ignoreCase: true,
  tolerateAudioTypos: true,
  allowPartialMatch: true,
  extraRules: ""
};

const DEFAULT_SYNC_OPTIONS = {
  mode: "manual",
  intervalMinutes: 60
};

const SOURCE_OPTIONS = [
  { value: "spreadsheet", label: "Planilha (CSV/XLSX)" },
  { value: "url_json", label: "URL JSON" },
  { value: "url_xml", label: "URL XML" },
  { value: "url_generic", label: "URL Genérica / Endpoint" },
  { value: "text", label: "Documento Texto" }
];

const MAPPING_FIELDS = [
  { key: "itemName", label: "Nome/Titulo" },
  { key: "price", label: "Preco" },
  { key: "prerequisite", label: "Pre-requisito" },
  { key: "description", label: "Descricao" },
  { key: "category", label: "Categoria" },
  { key: "aliases", label: "Aliases/Sinonimos" },
  { key: "notes", label: "Observacoes" },
  { key: "active", label: "Disponibilidade" },
  { key: "itemCode", label: "Codigo Externo" }
];

const EMPTY_OBJECT = {};

const ExternalKnowledgeBaseConfig = ({ values, setFieldValue }) => {
  const [detectedColumns, setDetectedColumns] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [previewItems, setPreviewItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);

  const externalDataConfig = useMemo(
    () => values.externalDataConfig || EMPTY_OBJECT,
    [values.externalDataConfig]
  );

  const sourceType = externalDataConfig.sourceType || "spreadsheet";
  const mapping = externalDataConfig.mapping || {};
  const searchOptions = {
    ...DEFAULT_SEARCH_OPTIONS,
    ...(externalDataConfig.searchOptions || {})
  };
  const syncOptions = {
    ...DEFAULT_SYNC_OPTIONS,
    ...(externalDataConfig.syncOptions || {})
  };

  const importedMeta = useMemo(() => {
    const meta = values.externalDataMeta || {};
    return {
      importedItemsCount: meta.importedItemsCount ?? externalDataConfig.importedItemsCount ?? 0,
      ignoredItemsCount: meta.ignoredItemsCount ?? externalDataConfig.ignoredItemsCount ?? 0,
      lastSyncAt: meta.lastSyncAt || externalDataConfig.lastSyncAt || null,
      lastSyncResult: meta.lastSyncResult || externalDataConfig.lastSyncResult || null
    };
  }, [values.externalDataMeta, externalDataConfig]);

  const updateConfig = (partial) => {
    setFieldValue("externalDataConfig", {
      ...externalDataConfig,
      searchOptions,
      syncOptions,
      ...partial
    });
  };

  const handleSourceTypeChange = (event) => {
    updateConfig({ sourceType: event.target.value });
  };

  const handleMappingChange = (field, value) => {
    updateConfig({
      mapping: {
        ...mapping,
        [field]: value
      }
    });
  };

  const handleSearchOptionChange = (field, value) => {
    updateConfig({
      searchOptions: {
        ...searchOptions,
        [field]: value
      }
    });
  };

  const handleSyncOptionChange = (field, value) => {
    updateConfig({
      syncOptions: {
        ...syncOptions,
        [field]: value
      }
    });
  };

  const buildConfigPayload = () => ({
    ...externalDataConfig,
    sourceType,
    mapping,
    searchOptions,
    syncOptions
  });

  const runPreview = async () => {
    setLoading(true);
    setError("");
    try {
      const payloadConfig = buildConfigPayload();
      const formData = new FormData();
      formData.append("config", JSON.stringify(payloadConfig));

      if (selectedFile) {
        formData.append("file", selectedFile);
      }

      const { data } = await api.post("/external-data/preview", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      setDetectedColumns(data.detectedColumns || []);
      setWarnings(data.warnings || []);
      setPreviewItems(data.preview || []);

      setFieldValue("externalDataConfig", data.config || payloadConfig);
      setFieldValue("externalDataItems", data.items || []);
      setFieldValue("externalDataMeta", {
        importedItemsCount: data.validItems,
        ignoredItemsCount: data.ignoredItems,
        lastSyncAt: data.config?.lastSyncAt,
        lastSyncResult: "success",
        warnings: data.warnings || []
      });
    } catch (err) {
      const backendMessage = err?.response?.data?.message;
      const finalMessage = backendMessage || "Nao foi possivel processar a fonte externa.";
      setError(finalMessage);
      setFieldValue("externalDataMeta", {
        ...values.externalDataMeta,
        lastSyncResult: "error",
        lastSyncAt: new Date().toISOString(),
        lastError: finalMessage
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper variant="outlined" style={{ padding: 16, marginTop: 16 }}>
      <Box mb={1}>
        <Typography variant="h6">Base Externa de Consulta</Typography>
        <Typography variant="body2" color="textSecondary">
          Configure fontes estruturadas para precos, requisitos e dados comerciais sem inflar o prompt.
        </Typography>
      </Box>

      <FormControlLabel
        control={(
          <Switch
            checked={Boolean(values.externalDataEnabled)}
            onChange={(e) => setFieldValue("externalDataEnabled", e.target.checked)}
            color="primary"
          />
        )}
        label="Ativar base externa"
      />

      {values.externalDataEnabled && (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Tipo de fonte</InputLabel>
                <Select label="Tipo de fonte" value={sourceType} onChange={handleSourceTypeChange}>
                  {SOURCE_OPTIONS.map(option => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {(sourceType === "url_json" || sourceType === "url_xml" || sourceType === "url_generic") && (
              <>
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    label="URL da fonte"
                    value={externalDataConfig.sourceUrl || ""}
                    onChange={(e) => updateConfig({ sourceUrl: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    label="Caminho raiz (opcional)"
                    value={externalDataConfig.rootPath || ""}
                    onChange={(e) => updateConfig({ rootPath: e.target.value })}
                    placeholder="ex: data.items"
                  />
                </Grid>
              </>
            )}

            {sourceType === "text" && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={5}
                  variant="outlined"
                  label="Conteudo textual"
                  value={externalDataConfig.sourceText || ""}
                  onChange={(e) => updateConfig({ sourceText: e.target.value })}
                />
              </Grid>
            )}

            {sourceType === "spreadsheet" && (
              <Grid item xs={12}>
                <Button component="label" variant="outlined">
                  Selecionar planilha (CSV/XLSX)
                  <input
                    hidden
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={(e) => {
                      const file = e.target.files && e.target.files[0];
                      setSelectedFile(file || null);
                      if (file) {
                        updateConfig({ sourceFileName: file.name });
                      }
                    }}
                  />
                </Button>
                <Typography variant="caption" style={{ marginLeft: 8 }}>
                  {selectedFile?.name || externalDataConfig.sourceFileName || "Nenhum arquivo selecionado"}
                </Typography>
              </Grid>
            )}
          </Grid>

          <Box mt={2} display="flex" gridGap={8}>
            <Button
              variant="contained"
              color="primary"
              disabled={loading}
              onClick={runPreview}
            >
              {loading ? <CircularProgress size={18} /> : "Testar fonte / Validar mapeamento"}
            </Button>
            <Button
              variant="outlined"
              disabled={loading}
              onClick={runPreview}
            >
              Atualizar agora
            </Button>
          </Box>

          {error && (
            <Box mt={2}>
              <Alert severity="error">{error}</Alert>
            </Box>
          )}

          {warnings.length > 0 && (
            <Box mt={2}>
              {warnings.map((warning, index) => (
                <Alert key={`${warning}_${index}`} severity="warning" style={{ marginBottom: 4 }}>
                  {warning}
                </Alert>
              ))}
            </Box>
          )}

          <Box mt={2}>
            <FormLabel component="legend">Mapeamento de dados</FormLabel>
            <Grid container spacing={2} style={{ marginTop: 4 }}>
              {MAPPING_FIELDS.map(field => (
                <Grid item xs={12} md={4} key={field.key}>
                  <FormControl fullWidth variant="outlined" size="small">
                    <InputLabel>{field.label}</InputLabel>
                    <Select
                      label={field.label}
                      value={mapping[field.key] || ""}
                      onChange={(e) => handleMappingChange(field.key, e.target.value)}
                    >
                      <MenuItem value="">
                        <em>Sem mapeamento</em>
                      </MenuItem>
                      {detectedColumns.map(column => (
                        <MenuItem key={column} value={column}>{column}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Box mt={2}>
            <FormLabel component="legend">Busca inteligente</FormLabel>
            <Grid container spacing={2} style={{ marginTop: 4 }}>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  variant="outlined"
                  size="small"
                  label="Confianca alta"
                  inputProps={{ min: 0.5, max: 1, step: 0.01 }}
                  value={searchOptions.confidenceThreshold}
                  onChange={(e) => handleSearchOptionChange("confidenceThreshold", Number(e.target.value))}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  variant="outlined"
                  size="small"
                  label="Confianca media"
                  inputProps={{ min: 0.3, max: 1, step: 0.01 }}
                  value={searchOptions.mediumConfidenceThreshold}
                  onChange={(e) => handleSearchOptionChange("mediumConfidenceThreshold", Number(e.target.value))}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  label="Regras complementares"
                  value={searchOptions.extraRules || ""}
                  onChange={(e) => handleSearchOptionChange("extraRules", e.target.value)}
                />
              </Grid>
            </Grid>

            <Box mt={1} display="flex" flexWrap="wrap">
              <FormControlLabel
                control={<Switch checked={Boolean(searchOptions.enabledFuzzy)} onChange={(e) => handleSearchOptionChange("enabledFuzzy", e.target.checked)} color="primary" />}
                label="Busca aproximada"
              />
              <FormControlLabel
                control={<Switch checked={Boolean(searchOptions.askConfirmationOnMedium)} onChange={(e) => handleSearchOptionChange("askConfirmationOnMedium", e.target.checked)} color="primary" />}
                label="Pedir confirmacao na confianca media"
              />
              <FormControlLabel
                control={<Switch checked={Boolean(searchOptions.enableAliases)} onChange={(e) => handleSearchOptionChange("enableAliases", e.target.checked)} color="primary" />}
                label="Permitir aliases"
              />
              <FormControlLabel
                control={<Switch checked={Boolean(searchOptions.ignoreAccents)} onChange={(e) => handleSearchOptionChange("ignoreAccents", e.target.checked)} color="primary" />}
                label="Ignorar acentos"
              />
              <FormControlLabel
                control={<Switch checked={Boolean(searchOptions.ignoreCase)} onChange={(e) => handleSearchOptionChange("ignoreCase", e.target.checked)} color="primary" />}
                label="Ignorar maiusculas/minusculas"
              />
              <FormControlLabel
                control={<Switch checked={Boolean(searchOptions.tolerateAudioTypos)} onChange={(e) => handleSearchOptionChange("tolerateAudioTypos", e.target.checked)} color="primary" />}
                label="Tolerar erros de transcricao"
              />
              <FormControlLabel
                control={<Switch checked={Boolean(searchOptions.allowPartialMatch)} onChange={(e) => handleSearchOptionChange("allowPartialMatch", e.target.checked)} color="primary" />}
                label="Busca por fragmento parcial"
              />
            </Box>
          </Box>

          <Box mt={2}>
            <FormLabel component="legend">Sincronizacao e cache</FormLabel>
            <Grid container spacing={2} style={{ marginTop: 4 }}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel>Modo de sincronizacao</InputLabel>
                  <Select
                    label="Modo de sincronizacao"
                    value={syncOptions.mode}
                    onChange={(e) => handleSyncOptionChange("mode", e.target.value)}
                  >
                    <MenuItem value="manual">Manual</MenuItem>
                    <MenuItem value="automatic">Automatico</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  variant="outlined"
                  size="small"
                  label="Intervalo (minutos)"
                  value={syncOptions.intervalMinutes}
                  onChange={(e) => handleSyncOptionChange("intervalMinutes", Number(e.target.value))}
                />
              </Grid>
            </Grid>
          </Box>

          <Box mt={2}>
            <Typography variant="body2" color="textSecondary">
              Ultima sincronizacao: {importedMeta.lastSyncAt ? new Date(importedMeta.lastSyncAt).toLocaleString() : "-"}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Resultado: {importedMeta.lastSyncResult || "-"}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Itens validos: {importedMeta.importedItemsCount} | Ignorados: {importedMeta.ignoredItemsCount}
            </Typography>
          </Box>

          {previewItems.length > 0 && (
            <Box mt={2}>
              <Typography variant="subtitle2">Previa de itens processados</Typography>
              <Box mt={1}>
                {previewItems.slice(0, 8).map(item => (
                  <Paper key={item.internalId} variant="outlined" style={{ padding: 8, marginBottom: 6 }}>
                    <Typography variant="body2"><strong>{item.name}</strong></Typography>
                    <Typography variant="caption" color="textSecondary">
                      Preco: {item.price ?? "-"} | Requisito: {item.prerequisite || "-"}
                    </Typography>
                  </Paper>
                ))}
              </Box>
            </Box>
          )}
        </>
      )}
    </Paper>
  );
};

export default ExternalKnowledgeBaseConfig;
